import { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Save, ArrowLeft } from 'lucide-react';
import api from '../../services/api';
import { toast } from 'react-hot-toast';

const PromptEditor = () => {
  const { scenarioId } = useParams();
  const navigate = useNavigate();
  const [scenario, setScenario] = useState(null);
  const [systemPrompt, setSystemPrompt] = useState('');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    loadScenario();
  }, [scenarioId]);

  const loadScenario = async () => {
    try {
      const response = await api.get(`/api/admin/scenarios/${scenarioId}`);
      setScenario(response.data.scenario);
      setSystemPrompt(response.data.scenario.system_prompt || '');
    } catch (error) {
      console.error('Error loading scenario:', error);
      toast.error('Ошибка загрузки сценария');
      navigate('/admin/scenarios');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    if (!systemPrompt.trim()) {
      toast.error('Промпт не может быть пустым');
      return;
    }

    setSaving(true);
    try {
      await api.put(`/api/admin/scenarios/${scenarioId}/prompt`, {
        systemPrompt: systemPrompt.trim(),
      });
      toast.success('Промпт успешно сохранен');
    } catch (error) {
      console.error('Error saving prompt:', error);
      toast.error('Ошибка сохранения промпта');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-blue-600 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-gray-600">Загрузка...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="p-8 h-full flex flex-col">
      {/* Header */}
      <div className="mb-6">
        <button
          onClick={() => navigate('/admin/scenarios')}
          className="flex items-center gap-2 text-gray-600 hover:text-gray-900 mb-4"
        >
          <ArrowLeft className="w-5 h-5" />
          Назад к сценариям
        </button>
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold text-gray-900">Редактор промпта</h1>
            <p className="text-gray-600 mt-1">
              Сценарий: <span className="font-medium">{scenario?.name}</span>
            </p>
          </div>
          <button
            onClick={handleSave}
            disabled={saving}
            className="flex items-center gap-2 px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:bg-blue-400"
          >
            <Save className="w-5 h-5" />
            {saving ? 'Сохранение...' : 'Сохранить'}
          </button>
        </div>
      </div>

      {/* Editor */}
      <div className="flex-1 bg-white rounded-xl border border-gray-200 overflow-hidden flex flex-col">
        <div className="p-4 border-b border-gray-200 bg-gray-50">
          <h2 className="text-sm font-semibold text-gray-700">System Prompt</h2>
          <p className="text-xs text-gray-500 mt-1">
            Этот промпт определяет поведение AI-персонажа в данном сценарии
          </p>
        </div>
        <div className="flex-1 p-4">
          <textarea
            value={systemPrompt}
            onChange={(e) => setSystemPrompt(e.target.value)}
            className="w-full h-full p-4 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 font-mono text-sm resize-none"
            placeholder="Введите system prompt для AI-персонажа..."
          />
        </div>
        <div className="p-4 border-t border-gray-200 bg-gray-50">
          <div className="flex items-center justify-between text-sm">
            <span className="text-gray-600">
              Символов: {systemPrompt.length}
            </span>
            <span className="text-gray-600">
              Строк: {systemPrompt.split('\n').length}
            </span>
          </div>
        </div>
      </div>

      {/* Tips */}
      <div className="mt-6 bg-blue-50 border border-blue-200 rounded-lg p-4">
        <h3 className="text-sm font-semibold text-blue-900 mb-2">💡 Советы по написанию промптов:</h3>
        <ul className="text-sm text-blue-800 space-y-1">
          <li>• Четко определите роль и характер персонажа</li>
          <li>• Укажите цели и задачи персонажа в разговоре</li>
          <li>• Опишите манеру общения и стиль речи</li>
          <li>• Добавьте контекст компании и ситуации</li>
          <li>• Укажите, как персонаж должен реагировать на разные ситуации</li>
        </ul>
      </div>
    </div>
  );
};

export default PromptEditor;
